import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Activity, Users, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, StatusBadge } from "@/command/ccUi";

const money = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const ROADMAP_STATUS = ["Planned", "In Progress", "Shipped"];

function Sparkline({ points, color = "var(--oraya-accent)" }) {
  if (!points || points.length < 2) return null;
  const w = 240, h = 48, max = Math.max(...points), min = Math.min(...points);
  const range = max - min || 1;
  const path = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p - min) / range) * (h - 6) - 3}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" preserveAspectRatio="none">
      <polyline points={path} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MetricStat({ label, value, icon: Icon }) {
  return (
    <div className="glass-medium rounded-xl p-4">
      <div className="flex items-center gap-2 text-subtle text-xs uppercase tracking-[0.14em]"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <p className="display text-2xl font-light text-ink mt-2">{value}</p>
    </div>
  );
}

export default function Products() {
  const { can } = useCommand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricModal, setMetricModal] = useState(null);
  const [roadModal, setRoadModal] = useState(null);
  const canCreate = can("products", "CREATE") || can("products", "MANAGE");
  const canEdit = can("products", "EDIT") || can("products", "MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/products/dashboard").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["products"], load);

  const saveMetric = async () => {
    try {
      const p = { ...metricModal };
      ["active_projects", "active_users", "uptime_pct", "mrr"].forEach((k) => { if (p[k] !== undefined && p[k] !== "") p[k] = Number(p[k]); });
      await api.post("/cc/products/metrics", p); toast.success("Metrics recorded"); setMetricModal(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveRoad = async () => {
    try {
      if (roadModal.id) await api.put(`/cc/products/roadmap/${roadModal.id}`, roadModal);
      else await api.post("/cc/products/roadmap", roadModal);
      toast.success("Saved"); setRoadModal(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delRoad = async (slug, id) => { if (!window.confirm("Delete roadmap item?")) return; await api.delete(`/cc/products/roadmap/${id}`); toast.success("Deleted"); load(); };

  if (loading) return <LoadingState label="Loading products…" />;

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Products</h1>
      <p className="text-subtle mt-1 mb-6">Live metrics and roadmap for ORAYA ecosystem products.</p>

      {(!data?.products || data.products.length === 0) ? (
        <EmptyState title="No products yet" />
      ) : data.products.map((p) => (
        <div key={p.slug} className="mb-8" data-testid={`product-${p.slug}`}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="display text-xl text-ink">{p.name}</h2>
              <p className="text-subtle text-sm">{p.tagline}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={p.status === "active" ? "healthy" : "info"} label={p.status === "active" ? "Active" : "Coming soon"} />
              {canCreate && <CCButton variant="ghost" onClick={() => setMetricModal({ product_slug: p.slug, date: new Date().toISOString().slice(0, 10) })} testid={`record-metrics-${p.slug}`}><Plus className="w-4 h-4" /> Record metrics</CCButton>}
            </div>
          </div>

          {p.latest ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid={`product-metrics-${p.slug}`}>
              <MetricStat label="Active Projects" value={p.latest.active_projects ?? "—"} icon={Activity} />
              <MetricStat label="Active Users" value={p.latest.active_users?.toLocaleString() ?? "—"} icon={Users} />
              <MetricStat label="Uptime" value={p.latest.uptime_pct != null ? `${p.latest.uptime_pct}%` : "—"} icon={Clock} />
              <MetricStat label="MRR" value={money(p.latest.mrr)} icon={TrendingUp} />
            </div>
          ) : (
            <EmptyState title="No metrics recorded yet" hint={canCreate ? "Record the first snapshot." : undefined} />
          )}

          {p.history && p.history.length > 1 && (
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <Panel title="MRR trend" testid={`mrr-trend-${p.slug}`}><Sparkline points={p.history.map((h) => h.mrr || 0)} /></Panel>
              <Panel title="Active users trend" testid={`users-trend-${p.slug}`}><Sparkline points={p.history.map((h) => h.active_users || 0)} /></Panel>
            </div>
          )}

          <Panel title="Roadmap" testid={`roadmap-${p.slug}`}
            action={canCreate && <CCButton variant="ghost" onClick={() => setRoadModal({ product_slug: p.slug, status: "Planned" })} testid={`roadmap-add-${p.slug}`}><Plus className="w-4 h-4" /> Add</CCButton>}>
            {(!p.roadmap || p.roadmap.length === 0) ? <EmptyState title="No roadmap items yet" /> : (
              <div className="flex flex-col gap-3">
                {p.roadmap.map((r) => (
                  <div key={r.id} className="p-4 rounded-xl glass-medium flex items-center justify-between gap-3" data-testid={`roadmap-item-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-ink font-medium">{r.title}</p>
                      <p className="text-xs text-subtle mt-0.5">{r.quarter}{r.description ? ` · ${r.description}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={r.status === "Shipped" ? "healthy" : r.status === "In Progress" ? "pending" : "info"} label={r.status} />
                      {canEdit && <>
                        <button onClick={() => setRoadModal({ ...r })} className="text-subtle hover:text-ink p-1.5"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => delRoad(p.slug, r.id)} className="text-subtle hover:text-red-500 p-1.5"><Trash2 className="w-4 h-4" /></button>
                      </>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      ))}

      {metricModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setMetricModal(null)} data-testid="metric-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">Record metrics</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Active projects"><input type="number" className={inputCls} value={metricModal.active_projects ?? ""} onChange={(e) => setMetricModal({ ...metricModal, active_projects: e.target.value })} data-testid="metric-field-projects" /></Field>
              <Field label="Active users"><input type="number" className={inputCls} value={metricModal.active_users ?? ""} onChange={(e) => setMetricModal({ ...metricModal, active_users: e.target.value })} data-testid="metric-field-users" /></Field>
              <Field label="Uptime %"><input type="number" step="0.01" className={inputCls} value={metricModal.uptime_pct ?? ""} onChange={(e) => setMetricModal({ ...metricModal, uptime_pct: e.target.value })} data-testid="metric-field-uptime" /></Field>
              <Field label="MRR (₹)"><input type="number" className={inputCls} value={metricModal.mrr ?? ""} onChange={(e) => setMetricModal({ ...metricModal, mrr: e.target.value })} data-testid="metric-field-mrr" /></Field>
              <Field label="Date"><input type="date" className={inputCls} value={metricModal.date || ""} onChange={(e) => setMetricModal({ ...metricModal, date: e.target.value })} data-testid="metric-field-date" /></Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setMetricModal(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={saveMetric} testid="metric-save">Save</CCButton>
            </div>
          </div>
        </div>
      )}

      {roadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setRoadModal(null)} data-testid="roadmap-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">{roadModal.id ? "Edit" : "Add"} roadmap item</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title"><input className={inputCls} value={roadModal.title || ""} onChange={(e) => setRoadModal({ ...roadModal, title: e.target.value })} data-testid="roadmap-field-title" /></Field>
              <Field label="Quarter"><input className={inputCls} value={roadModal.quarter || ""} onChange={(e) => setRoadModal({ ...roadModal, quarter: e.target.value })} data-testid="roadmap-field-quarter" /></Field>
              <Field label="Status">
                <select className={inputCls} value={roadModal.status || "Planned"} onChange={(e) => setRoadModal({ ...roadModal, status: e.target.value })} data-testid="roadmap-field-status">
                  {ROADMAP_STATUS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <div className="col-span-2"><Field label="Description"><input className={inputCls} value={roadModal.description || ""} onChange={(e) => setRoadModal({ ...roadModal, description: e.target.value })} data-testid="roadmap-field-desc" /></Field></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setRoadModal(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={saveRoad} testid="roadmap-save">Save</CCButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
